/**
 * Shared design tokens for accessible map room status encoding (ADR-097).
 *
 * Single source of truth for the retuned safety palette, non-color
 * encodings (pattern fills, icon set, border-style definitions), and the
 * top-right split-pill badge sizing/zoom-threshold constants.
 *
 * Referenced by all three AIM map clients (Web / iOS / Android) per
 * ADR-090 Policy 6.
 */

export type SafetyStatus = 'safe' | 'unsafe' | 'cleared' | 'evacuate' | null;

/**
 * Pattern fill identifiers — density tracks severity so the encoding reads
 * in grayscale. `none` is used for null/unknown rooms.
 */
export type PatternFill = 'solid' | 'dots-sparse' | 'stripes-diagonal' | 'crosshatch-dense' | 'none';

/**
 * Border style identifiers.
 *   - `solid-default` — normal teal perimeter
 *   - `solid-medical` — thicker blue perimeter (legacy color-only encoding)
 *   - `dashed-medical` — long-dash pattern (accessible encoding)
 */
export type BorderStyle = 'solid-default' | 'solid-medical' | 'dashed-medical';

/**
 * Safety icon identifiers. Mapped to SVG assets / glyph names on each client.
 */
export type SafetyIcon =
  | 'shield-check'     // safe
  | 'warning-triangle' // unsafe
  | 'broom'            // cleared
  | 'exit-arrow'       // evacuate
  | 'question-hollow'  // unknown / null
  ;

/**
 * Full rendering spec for a single room. Platforms layer the encodings
 * they support (all render `color` + `icon`; only pattern-capable
 * renderers use `pattern`; only dash-capable renderers use `borderStyle`).
 */
export interface RoomRenderSpec {
  status: SafetyStatus;
  needsMedicalAttention: boolean;
  color: string;
  pattern: PatternFill;
  icon: SafetyIcon;
  borderStyle: BorderStyle;
  accessibleName: string;
}

/**
 * Retuned safety palette.
 *
 * The original light tints ("#C0E7B7", "#E8B4AA", "#D2DEF2") almost certainly
 * failed WCAG 2.1 SC 1.4.11 (Non-text Contrast, 3:1). These values are
 * darkened to meet the 3:1 threshold against a white building underlay.
 *
 * Contrast ratios vs #FFFFFF (white underlay):
 *   safe    (#5FA255) ≈ 3.0:1
 *   unsafe  (#B4483C) ≈ 5.0:1
 *   cleared (#3A6FBF) ≈ 5.1:1
 *   evacuate(#C85A1F) ≈ 3.7:1
 */
export const AccessibleSafetyColors = {
  safe: '#5FA255',
  unsafe: '#B4483C',
  cleared: '#3A6FBF',
  evacuate: '#C85A1F',
  unknown: '#FFFFFF',
  /** Medical border color (dashed/thick) — same hue as legacy but retuned for contrast. */
  medical: '#1D3FBA',
  /** High-contrast outer ring on Mahere canvas — rendered as a thin halo. */
  outerRing: '#0D1F3D',
} as const;

/**
 * Legacy palette. Kept here as the identity-case fallback so default
 * rendering (feature flag / preference off) remains byte-identical to the
 * behavior shipping before ADR-097.
 */
export const LegacySafetyColors = {
  safe: '#B7E8B2',
  unsafe: '#F2B1A8',
  cleared: '#CFDEF4',
  evacuate: '#F5A623',
  unknown: '#FFFFFF',
  medical: '#395FE6',
} as const;

/**
 * Pattern → SVG / path definitions shared across clients.
 *
 * On web, these are serialized into off-screen canvases used with
 * `CanvasRenderingContext2D.createPattern()`. On Mapbox (Android), the same
 * SVGs feed `fill-pattern` sprites. iOS consumes the SVG paths via
 * CoreGraphics patterns when the Mahere SDK supports them.
 */
export const PatternDefinitions: Record<PatternFill, {
  tileSize: number;
  svg: string;
}> = {
  solid: {
    tileSize: 8,
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><rect width="8" height="8" fill="currentColor"/></svg>',
  },
  'dots-sparse': {
    tileSize: 14,
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><rect width="14" height="14" fill="currentColor"/><circle cx="7" cy="7" r="1.5" fill="#000" fill-opacity="0.35"/></svg>',
  },
  'stripes-diagonal': {
    tileSize: 10,
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><rect width="10" height="10" fill="currentColor"/><path d="M-2 12 L12 -2" stroke="#000" stroke-opacity="0.4" stroke-width="2"/></svg>',
  },
  'crosshatch-dense': {
    tileSize: 8,
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><rect width="8" height="8" fill="currentColor"/><path d="M-1 9 L9 -1 M-1 -1 L9 9" stroke="#000" stroke-opacity="0.45" stroke-width="1.5"/></svg>',
  },
  none: {
    tileSize: 1,
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"><rect width="1" height="1" fill="currentColor"/></svg>',
  },
};

/**
 * Canonical status → pattern mapping. Density tracks severity:
 *   safe     → solid         (lowest density — lowest severity)
 *   cleared  → sparse dots
 *   unsafe   → diagonal stripes
 *   evacuate → dense crosshatch (highest density — highest severity)
 */
export function patternForStatus(status: SafetyStatus): PatternFill {
  switch (status) {
    case 'safe': return 'solid';
    case 'cleared': return 'dots-sparse';
    case 'unsafe': return 'stripes-diagonal';
    case 'evacuate': return 'crosshatch-dense';
    default: return 'none';
  }
}

/**
 * Canonical status → icon mapping.
 */
export function iconForStatus(status: SafetyStatus): SafetyIcon {
  switch (status) {
    case 'safe': return 'shield-check';
    case 'unsafe': return 'warning-triangle';
    case 'cleared': return 'broom';
    case 'evacuate': return 'exit-arrow';
    default: return 'question-hollow';
  }
}

/**
 * Canonical medical → border-style mapping.
 */
export function borderStyleForMedical(needsMedicalAttention: boolean): BorderStyle {
  return needsMedicalAttention ? 'dashed-medical' : 'solid-default';
}

/**
 * Pill marker sizing + placement rules. Identical across platforms so the
 * visual weight matches (per ADR-090 Policy 6).
 */
export const PillSpec = {
  /** Full pill width in CSS pixels (half-width per half). */
  width: 44,
  height: 22,
  /** Corner radius. */
  radius: 11,
  /** Inset from room's top-right corner (px). */
  inset: 6,
  /** Icon glyph size inside each half (px). */
  iconSize: 14,
  /** Divider width between the two halves (px). */
  dividerWidth: 1,
  /** Pill background + divider + stroke colors. */
  background: '#FFFFFF',
  divider: '#00000022',
  stroke: '#0D1F3D',
  medicalCrossColor: '#B4483C',
} as const;

/**
 * Zoom thresholds. The pill is only rendered when the map is zoomed in far
 * enough that per-room geometry is meaningfully visible.
 *
 * Mahere canvas renders rooms from zoom 18+, so the pill threshold sits at
 * the same boundary. On MapsIndoors/GMSPolygon renderers (no pattern/dash
 * capability), the pill is the primary encoding — so it uses a slightly
 * lower threshold to widen the accessible zoom range.
 */
export const PillZoomThresholds = {
  mahere: 19,
  mapsIndoorsPolygon: 18,
} as const;

/**
 * Legend entries rendered by the Legend panel component. Order reflects
 * reading order in the panel (most severe last).
 */
export const LegendEntries: Array<{
  status: SafetyStatus;
  label: string;
  pattern: PatternFill;
  icon: SafetyIcon;
  color: string;
}> = [
  { status: null, label: 'Unknown', pattern: 'none', icon: 'question-hollow', color: AccessibleSafetyColors.unknown },
  { status: 'safe', label: 'Safe', pattern: 'solid', icon: 'shield-check', color: AccessibleSafetyColors.safe },
  { status: 'cleared', label: 'Cleared', pattern: 'dots-sparse', icon: 'broom', color: AccessibleSafetyColors.cleared },
  { status: 'unsafe', label: 'Unsafe', pattern: 'stripes-diagonal', icon: 'warning-triangle', color: AccessibleSafetyColors.unsafe },
  { status: 'evacuate', label: 'Evacuate', pattern: 'crosshatch-dense', icon: 'exit-arrow', color: AccessibleSafetyColors.evacuate },
];

/**
 * Build a full RoomRenderSpec from inputs. This is the canonical spec
 * factory — every client calls this (directly or via a thin adapter) so
 * the three renderers derive identical style specs from identical inputs.
 */
export function buildRoomRenderSpec(
  status: SafetyStatus,
  needsMedicalAttention: boolean,
  options: { accessible: boolean } = { accessible: false },
): RoomRenderSpec {
  const palette = options.accessible ? AccessibleSafetyColors : LegacySafetyColors;
  const color = (() => {
    switch (status) {
      case 'safe': return palette.safe;
      case 'unsafe': return palette.unsafe;
      case 'cleared': return palette.cleared;
      case 'evacuate': return (palette as typeof AccessibleSafetyColors).evacuate;
      default: return palette.unknown;
    }
  })();

  const accessibleName = (() => {
    const statusLabel = status ?? 'unknown';
    const medicalLabel = needsMedicalAttention ? ', medical attention needed' : '';
    return `${statusLabel}${medicalLabel}`;
  })();

  return {
    status,
    needsMedicalAttention,
    color,
    pattern: options.accessible ? patternForStatus(status) : 'none',
    icon: iconForStatus(status),
    borderStyle: options.accessible
      ? borderStyleForMedical(needsMedicalAttention)
      : (needsMedicalAttention ? 'solid-medical' : 'solid-default'),
    accessibleName,
  };
}
